import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, Box, Typography, Chip, Avatar,
  CircularProgress, Divider, Slide, IconButton,
  useTheme, useMediaQuery,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  CheckCircle as CheckIcon,
  RadioButtonChecked as RadioIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Store as StoreIcon,
  LocalShipping as DeliveryIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import config from '../config';
import { useEstados } from '../context/EstadosContext';
import { useEmpresa } from '../context/EmpresaContext';

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

// Detect service type
const detectType = (cab) => {
  if (!cab) return { key: 'fijo', label: 'Fijo', icon: StoreIcon, color: '#1A237E' };
  const esDelivery = cab.es_delivery || cab.tipo_servicio?.toLowerCase().includes('delivery');
  if (esDelivery && !cab.id_orden_padre) return { key: 'delivery', label: 'Delivery Master', icon: DeliveryIcon, color: '#FF6B35' };
  if (esDelivery && cab.id_orden_padre) return { key: 'delivery_hijo', label: 'Entrega Delivery', icon: DeliveryIcon, color: '#9C27B0' };
  const tipo = (cab.tipo_servicio || '').toLowerCase();
  if (tipo.includes('express')) return { key: 'express', label: 'Express', icon: LocationIcon, color: '#E65100' };
  if (tipo.includes('movil') || tipo.includes('móvil') || tipo.includes('domicilio')) return { key: 'movil', label: 'A Domicilio', icon: LocationIcon, color: '#0277BD' };
  return { key: 'fijo', label: 'En Local', icon: StoreIcon, color: '#1A237E' };
};

const DetalleDialog = ({ open, onClose, ordenId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { obtenerColorEstado } = useEstados();
  const { simboloMoneda } = useEmpresa();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const moneda = simboloMoneda || 'S/';

  useEffect(() => {
    if (open && ordenId) {
      setLoading(true);
      axios.get(`${config.API_BASE_URL}/api/ordenes/${ordenId}/detalle-completo`, {
        headers: { Authorization: `Bearer ${user.token}` }
      }).then(res => { setData(res.data); setLoading(false); })
        .catch(() => setLoading(false));
    }
  }, [open, ordenId]);

  if (!open) return null;

  const cab = data?.cabecera;
  const detalles = data?.detalles || [];
  const trabajadores = data?.trabajadores || [];
  const historial = data?.historial || [];
  const entregas = data?.entregas || [];
  const typeInfo = detectType(cab);
  const TypeIcon = typeInfo.icon;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      TransitionComponent={isMobile ? Transition : undefined}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3, maxHeight: isMobile ? '100%' : '90vh' } }}
    >
      {/* Header */}
      {loading || !cab ? (
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <CircularProgress size={32} />
          <Typography sx={{ mt: 2, color: '#94A3B8', fontSize: 13 }}>Cargando detalle...</Typography>
        </Box>
      ) : (
        <>
          <Box sx={{
            background: `linear-gradient(135deg, ${typeInfo.color} 0%, ${typeInfo.color}CC 100%)`,
            color: '#fff', px: 2.5, py: 2, position: 'relative',
          }}>
            <IconButton onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8, color: 'rgba(255,255,255,0.7)' }}>
              <CloseIcon />
            </IconButton>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <TypeIcon sx={{ fontSize: 26 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 800 }}>
                  OS #{cab.id_orden_servicio}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.3, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<TypeIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
                    label={cab.tipo_servicio || typeInfo.label}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 700, fontSize: 11, height: 22 }}
                  />
                  <Typography sx={{ fontSize: 11, opacity: 0.7 }}>
                    {cab.mes}/{cab.anio}
                  </Typography>
                </Box>
              </Box>
              <Chip label={cab.estado_orden} sx={{
                bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700, fontSize: 12,
              }} />
            </Box>

            {/* Delivery master badge */}
            {typeInfo.key === 'delivery' && (
              <Chip label={`MASTER · ${entregas.length} entregas`} size="small"
                sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700, fontSize: 11 }} />
            )}
            {typeInfo.key === 'delivery_hijo' && cab.id_orden_padre && (
              <Chip label={`Hijo de OS #${cab.id_orden_padre}`} size="small"
                sx={{ mt: 1, bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700, fontSize: 11 }} />
            )}
          </Box>

          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

              {/* Cliente */}
              {cab.nombre_cliente && (
                <Section title="Cliente" icon={<PersonIcon />} color="#4FC3F7">
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#1E293B' }}>
                    {cab.nombre_cliente} {cab.apellido_cliente || ''}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                    {cab.telefono_cliente && (
                      <InfoChip icon={<PhoneIcon />} text={cab.telefono_cliente} />
                    )}
                    {cab.email_cliente && (
                      <InfoChip icon={<EmailIcon />} text={cab.email_cliente} />
                    )}
                  </Box>
                </Section>
              )}

              {/* Ubicación */}
              <Section title="Ubicación" icon={<StoreIcon />} color="#FFA726">
                {cab.nombre_local && (
                  <Row label="Local" value={cab.nombre_local} />
                )}
                {cab.direccion_local && (
                  <Row label="Dirección" value={cab.direccion_local} />
                )}
                {cab.direccion_origen && cab.direccion_origen !== cab.direccion_local && (
                  <Row label="Origen" value={cab.direccion_origen} />
                )}
              </Section>

              {/* Delivery: Entregas */}
              {typeInfo.key === 'delivery' && entregas.length > 0 && (
                <Section title={`Entregas (${entregas.length})`} icon={<DeliveryIcon />} color="#FF6B35">
                  {entregas.map((e, i) => (
                    <Box key={i} sx={{
                      display: 'flex', alignItems: 'center', gap: 1, py: 0.8,
                      borderBottom: i < entregas.length - 1 ? '1px solid #F1F5F9' : 'none',
                    }}>
                      <Box sx={{
                        width: 24, height: 24, borderRadius: '50%',
                        bgcolor: e.entregado ? '#10B981' : '#FF9800',
                        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 800, flexShrink: 0,
                      }}>
                        {e.orden_secuencia || i + 1}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }} noWrap>
                          {e.nombre_cliente || e.nombre_cliente_destino || 'Cliente'}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#94A3B8' }} noWrap>
                          {e.direccion || e.direccion_origen || 'Sin dirección'}
                        </Typography>
                      </Box>
                      {e.monto_entrega > 0 && (
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#10B981' }}>
                          {moneda} {parseFloat(e.monto_entrega).toFixed(2)}
                        </Typography>
                      )}
                      <Chip label={e.entregado ? 'Entregado' : 'Pendiente'} size="small" sx={{
                        height: 20, fontSize: 10, fontWeight: 700,
                        bgcolor: e.entregado ? '#ECFDF5' : '#FFF7ED',
                        color: e.entregado ? '#059669' : '#D97706',
                      }} />
                    </Box>
                  ))}
                </Section>
              )}

              {/* Servicios */}
              {detalles.length > 0 && (
                <Section title="Servicios" icon={<CheckIcon />} color="#66BB6A">
                  {detalles.map((d, i) => (
                    <Box key={i} sx={{
                      display: 'flex', alignItems: 'center', gap: 1, py: 0.5,
                      borderBottom: i < detalles.length - 1 ? '1px solid #F1F5F9' : 'none',
                    }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1E293B', flex: 1 }}>
                        {d.nombre_servicio}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>x{d.cantidad}</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1E293B' }}>
                        {moneda} {(d.cantidad * d.precio).toFixed(2)}
                      </Typography>
                    </Box>
                  ))}
                  {cab.precio_total > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, mt: 0.5, borderTop: '2px solid #E2E8F0' }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1A237E' }}>Total</Typography>
                      <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#1A237E' }}>
                        {moneda} {parseFloat(cab.precio_total).toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                </Section>
              )}

              {/* Equipo */}
              {trabajadores.length > 0 && (
                <Section title="Equipo" icon={<PersonIcon />} color="#7E57C2">
                  {trabajadores.map((t, i) => {
                    const gana = t.comision_flat > 0 ? t.comision_flat : (t.precio_servicio * t.comision / 100);
                    return (
                      <Box key={i} sx={{
                        display: 'flex', alignItems: 'center', gap: 1, py: 0.8,
                        borderBottom: i < trabajadores.length - 1 ? '1px solid #F1F5F9' : 'none',
                      }}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: 12, fontWeight: 800, bgcolor: '#7E57C2' }}>
                          {(t.nombre_trabajador || '')[0]}{(t.apellido_trabajador || '')[0]}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }} noWrap>
                              {t.nombre_trabajador} {t.apellido_trabajador}
                            </Typography>
                            {t.es_responsable && <StarIcon sx={{ fontSize: 14, color: '#FFC107' }} />}
                          </Box>
                          <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>
                            {t.telefono_trabajador}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
                            {moneda} {parseFloat(t.precio_servicio || 0).toFixed(2)}
                          </Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#7E57C2' }}>
                            Gana {moneda} {gana.toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Section>
              )}

              {/* Timeline */}
              {historial.length > 0 && (
                <Section title="Línea de Tiempo" icon={<TimeIcon />} color="#1A237E">
                  {historial.map((log, i) => {
                    const color = obtenerColorEstado(log.id_estado_servicio) || '#78909C';
                    let duracion = null;
                    if (i < historial.length - 1) {
                      const diff = new Date(historial[i + 1].fecha_real) - new Date(log.fecha_real);
                      const h = Math.floor(diff / 3600000);
                      const d = Math.floor(h / 24);
                      if (d > 0) duracion = `${d}d ${h % 24}h`;
                      else if (h > 0) duracion = `${h}h`;
                      else duracion = `${Math.floor(diff / 60000)}min`;
                    }

                    return (
                      <Box key={i} sx={{ display: 'flex', gap: 1.5, pb: i < historial.length - 1 ? 2 : 0, position: 'relative' }}>
                        {/* Line */}
                        {i < historial.length - 1 && (
                          <Box sx={{
                            position: 'absolute', left: 11, top: 28, bottom: 0,
                            width: 2, bgcolor: '#E2E8F0',
                          }} />
                        )}
                        {/* Dot */}
                        <Box sx={{
                          width: 24, height: 24, borderRadius: '50%', bgcolor: color,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0, zIndex: 1,
                        }}>
                          {i === historial.length - 1
                            ? <RadioIcon sx={{ fontSize: 14, color: '#fff' }} />
                            : <CheckIcon sx={{ fontSize: 14, color: '#fff' }} />}
                        </Box>
                        {/* Content */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Chip label={log.nombre_estado_mostrar || log.descripcion_estado} size="small" sx={{
                              bgcolor: color, color: '#fff', fontWeight: 700, fontSize: 11, height: 22,
                            }} />
                            {duracion && (
                              <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>{duracion}</Typography>
                            )}
                          </Box>
                          <Typography sx={{ fontSize: 11, color: '#64748B', mt: 0.3 }}>
                            {new Date(log.fecha_real).toLocaleString('es-PE', {
                              day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                            })}
                          </Typography>
                          {log.glosa && (
                            <Typography sx={{ fontSize: 12, color: '#64748B', mt: 0.5, fontStyle: 'italic' }}>
                              "{log.glosa}"
                            </Typography>
                          )}
                          {/* Imagen adjunta */}
                          {log.imagen_url && (
                            <Box sx={{ mt: 1 }}>
                              <img
                                src={`${config.API_BASE_URL}${log.imagen_url}`}
                                alt="Evidencia"
                                style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8, border: '1px solid #E2E8F0' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            </Box>
                          )}
                          {/* Campos dinámicos */}
                          {log.campos_dinamicos?.length > 0 && (
                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {log.campos_dinamicos.map((c, ci) => {
                                if (c.tipo_campo === 'imagen' && c.archivo_url) {
                                  return (
                                    <img key={ci}
                                      src={`${config.API_BASE_URL}${c.archivo_url}`}
                                      alt={c.etiqueta_display}
                                      style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 8, border: '1px solid #E2E8F0' }}
                                      onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                  );
                                }
                                return (
                                  <Box key={ci} sx={{
                                    px: 1.5, py: 0.5, bgcolor: '#F1F5F9', borderRadius: 2,
                                    borderLeft: `3px solid ${color}`,
                                  }}>
                                    <Typography sx={{ fontSize: 10, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase' }}>
                                      {c.etiqueta_display}
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1E293B' }}>
                                      {c.tipo_campo === 'numero' && c.valor_campo
                                        ? `${moneda} ${parseFloat(c.valor_campo).toFixed(2)}`
                                        : c.valor_campo || '-'}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Section>
              )}
            </Box>
          </DialogContent>

          {/* Footer */}
          <Box sx={{ borderTop: '1px solid #E2E8F0', p: 2 }}>
            <Box onClick={onClose} sx={{
              py: 1.2, borderRadius: 2.5, textAlign: 'center',
              background: `linear-gradient(135deg, ${typeInfo.color}, ${typeInfo.color}CC)`,
              cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#fff',
              '&:hover': { opacity: 0.9 },
            }}>
              Cerrar
            </Box>
          </Box>
        </>
      )}
    </Dialog>
  );
};

// Section card component
const Section = ({ title, icon, color, children }) => (
  <Box sx={{
    border: '1px solid #E2E8F0', borderRadius: 3, overflow: 'hidden',
  }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
      bgcolor: `${color}08`, borderBottom: '1px solid #F1F5F9',
    }}>
      <Box sx={{
        width: 28, height: 28, borderRadius: 1.5,
        background: `linear-gradient(135deg, ${color}, ${color}CC)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 16 } })}
      </Box>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1E293B', textTransform: 'uppercase', letterSpacing: 0.3 }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ px: 1.5, py: 1.5 }}>
      {children}
    </Box>
  </Box>
);

// Small info chip
const InfoChip = ({ icon, text }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    {React.cloneElement(icon, { sx: { fontSize: 14, color: '#94A3B8' } })}
    <Typography sx={{ fontSize: 12, color: '#64748B' }}>{text}</Typography>
  </Box>
);

// Label + Value row
const Row = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 1, py: 0.3 }}>
    <Typography sx={{ fontSize: 12, color: '#94A3B8', minWidth: 60 }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1E293B', flex: 1 }}>{value}</Typography>
  </Box>
);

export default DetalleDialog;
